// The financing line, drawn here rather than by Shopify's own widget.
//
// Shopify's <shopify-payment-terms> is handed one variant and one quantity and
// nothing else, so on a page where the buyer is assembling a rig it quotes the
// bench on its own and never moves when an attachment goes in. Shop Pay splits
// the whole order at checkout, so the honest figure on the page is the one the
// cart will carry when the button is pressed.
//
// The plan is Shopify's, not ours. These bands are theirs, read 23 Sep 2026
// from help.shopify.com/en/manual/payments/shop-pay-installments, for the
// United States:
//
//   under $35        not offered at all
//   $35 to $49.99    two interest-free payments, a fortnight apart
//   $50 to $999.99   four interest-free payments, a fortnight apart
//   $1,000 and over  monthly, and interest-bearing at a rate only the buyer's
//                    own approval settles, so no figure can be promised here
//   over $30,000     not offered at all
//
// Outside the interest-free bands nothing quotes a number. A page that cannot
// say what the payment is says so rather than inventing one.

const initApexFinancing = ($el) => {
  const NOT_OFFERED_BELOW = 3500;
  const TWO_PAYMENTS_BELOW = 5000;
  const INTEREST_FREE_UP_TO = 99999;
  const NOT_OFFERED_ABOVE = 3000000;

  const wording = utils.JSONParse($el.querySelector("[data-apex-financing-text]")?.innerHTML) ?? {};

  const cartTotal = () => window._stores?.cart?.state?.total_price ?? 0;

  // The builder publishes what the page will cost with the cart already counted
  // in, so a bench with a rig staged against it is not quoted on the bench.
  const stagedTotal = () => window._stores?.apexBuildTotal?.value ?? null;

  // Every row of extras on the page publishes what it has ticked. Read off the
  // rows rather than off the form's own upsell list, which is a Map and so not
  // something every build of the framework watches for changes.
  const addonTotal = () => {
    const rows = window._stores?.apexAddonTotals?.rows;
    if (!rows) return 0;
    return Object.values(rows).reduce((total, value) => total + value, 0);
  };

  const formTotal = (state) => {
    if (!state) return 0;
    const quantity = state.quantity ?? 1;
    const variantPrice = state.selected_variant?.price ?? 0;
    // Every extra the button sends goes out at the main quantity, so it is
    // counted the same way here.
    return (variantPrice + addonTotal()) * quantity;
  };

  const getTotal = (state) => stagedTotal() ?? cartTotal() + formTotal(state);

  const getPlan = (state) => {
    const total = getTotal(state);
    if (total < NOT_OFFERED_BELOW || total > NOT_OFFERED_ABOVE) return null;
    if (total > INTEREST_FREE_UP_TO) return { payments: 0, amount: 0 };
    const payments = total < TWO_PAYMENTS_BELOW ? 2 : 4;
    // Rounded up, never down. Shop Pay puts whatever is left over on the first
    // payment, so a figure rounded down would be one the buyer never pays.
    return { payments, amount: Math.ceil(total / payments) };
  };

  // The figure is set apart from the words around it, the way Shopify's own
  // widget sets it apart, so the wording is handed back in the three pieces the
  // markup draws rather than as one string it would have to cut up itself.
  const getFinancingLine = (state) => {
    const plan = getPlan(state);
    if (!plan) return null;
    if (!plan.payments) return { before: wording.over_time ?? "", amount: "", after: "" };
    const [before = "", after = ""] = (wording.per_month ?? "").split("[amount]");
    return { before, amount: utils.formatMoney(plan.amount), after };
  };

  return { getFinancingLine };
};

window._sections = window._sections || {};
window._sections["initApexFinancing"] = initApexFinancing;
